import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../ui/tooltip';
import { Separator } from '../../ui/separator';
import {
  MapPin,
  History,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import InventoryFreshnessIndicator from './InventoryFreshnessIndicator';
import { POSourceList } from './POSourceChip';
import {
  getFreshnessColor,
  getStockLevelColor
} from '../../../utils/inventoryHelpers';

/**
 * LocationStockCard Component
 * Displays location-specific inventory with freshness, sources, and selection
 */
function LocationStockCard({
  location,
  product,
  onSelect,
  onViewHistory,
  selected = false,
  showSourceInfo = true,
  disabled = false
}) {
  if (!location) return null;

  const hasStock = location.available_quantity > 0;
  const isStale = location.data_freshness === 'stale';
  const stockColor = getStockLevelColor(location.available_quantity);
  const freshnessColor = location.last_movement?.created_at
    ? getFreshnessColor(location.last_movement.created_at)
    : '#9ca3af';

  const handleSelect = () => {
    if (!disabled && hasStock) {
      onSelect(location);
    }
  };

  const handleHistoryClick = (e) => {
    e.stopPropagation();
    if (onViewHistory && product) {
      onViewHistory(product.product_id || product.product_code, location.location);
    }
  };

  const getStockBadgeVariant = () => {
    if (stockColor === 'success') return 'default';
    if (stockColor === 'warning') return 'secondary';
    return 'destructive';
  };

  return (
    <Card
      onClick={handleSelect}
      className={`mb-4 transition-all duration-200
        ${disabled || !hasStock ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5'}
        ${selected ? 'bg-primary/10 border-l-4 border-l-blue-500' : ''}`}
      style={{ borderLeftColor: selected ? undefined : freshnessColor, borderLeftWidth: '4px' }}
    >
      <CardContent className="p-4">
        {/* Header with location name and selection radio */}
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <MapPin className={`h-5 w-5 ${hasStock ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className="text-lg font-semibold">
                {location.location || '默认位置'}
              </span>
              <Badge variant={getStockBadgeVariant()} className="font-bold">
                {location.available_quantity}{location.unit || product?.unit || '支'}
              </Badge>
              {!hasStock && (
                <Badge variant="destructive" className="border">无库存</Badge>
              )}
            </div>
          </div>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              selected ? 'border-blue-500' : 'border-gray-300'
            } ${disabled || !hasStock ? 'opacity-50' : 'cursor-pointer'}`}
          >
            {selected && <div className="w-3 h-3 rounded-full bg-primary/100" />}
          </div>
        </div>

        {/* Stock statistics */}
        <div className="flex gap-4 mt-4 mb-4">
          <div className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4 text-success" />
            <span className="text-xs text-muted-foreground">入库: {location.inbound_total || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingDown className="h-4 w-4 text-destructive" />
            <span className="text-xs text-muted-foreground">出库: {location.outbound_total || 0}</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">移动记录: {location.movement_count || 0}</span>
          </div>
        </div>

        <Separator className="my-2" />

        {/* Freshness indicator and history button */}
        <div className={`flex justify-between items-center ${showSourceInfo ? 'mb-4' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">最后更新:</span>
            <InventoryFreshnessIndicator
              timestamp={location.last_movement?.created_at}
              variant="inline"
              size="small"
            />
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleHistoryClick}
                  className="h-8 w-8"
                >
                  <History className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>查看库存历史</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Warning for stale data */}
        {isStale && (
          <div className={`p-2 ${showSourceInfo ? 'mb-4' : ''} bg-warning/20 rounded border border-yellow-400`}>
            <span className="text-xs text-warning">
              ⚠️ 数据已超过7天未更新，建议使用前验证实际库存
            </span>
          </div>
        )}

        {/* PO source information */}
        {showSourceInfo && location.source_origins && location.source_origins.length > 0 && (
          <div>
            <span className="text-xs text-muted-foreground block mb-1">库存来源:</span>
            <POSourceList
              sources={location.source_origins}
              maxVisible={2}
              size="small"
              showQuantity={true}
            />
          </div>
        )}

        {/* Last movement info (if available) */}
        {location.last_movement && (
          <div className="mt-2">
            <span className="text-xs text-muted-foreground">
              最近操作: {location.last_movement.movement_type === 'inbound' ? '入库' : '出库'}{' '}
              {location.last_movement.quantity ? `${location.last_movement.quantity}支` : ''}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LocationStockCard;
